import { useEffect, useMemo, useRef, useState } from 'react';
import { DigitalTwin } from 'types/dt-graphql-apollo-v2';
import { useStartConsistencyJob } from 'hooks/useStartConsistencyJob';
import { ViewOption } from './components/SchemaFilterList';
import { useDataConsistency } from './hooks/useDataConsistency';
import { useSchemaData } from './useSchemaData';
import {
  ReportData,
  parseReportTemplate,
} from './components/useRunConsistencyAttributesSelection';

const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const TERMINAL_STATUSES = ['SUCCESS', 'ERROR'];

export const useDataConsistencyPage = (digitalTwin: DigitalTwin) => {
  const { schemas } = useSchemaData(digitalTwin);
  const [polling, setPolling] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const schemaNameById = useMemo(
    () =>
      Object.fromEntries(
        schemas.map(({ schemaCollection: sc }) => [sc.externalId, sc.name])
      ),
    [schemas]
  );

  const dataConsistency = useDataConsistency(
    digitalTwin.externalId,
    digitalTwin.tenant,
    schemaNameById,
    polling
  );

  const { startConsistencyJob, isRunning: isGeneratingReport } =
    useStartConsistencyJob();

  useEffect(() => {
    if (!polling) return;
    const status = dataConsistency.selectedCollection?.lastRun?.status;
    if (status && TERMINAL_STATUSES.includes(status)) {
      setPolling(false);
      clearTimeout(pollTimerRef.current);
    }
  }, [polling, dataConsistency.selectedCollection?.lastRun?.status]);

  useEffect(() => () => clearTimeout(pollTimerRef.current), []);

  const [showCollectionInfo, setShowCollectionInfo] = useState(false);
  const [viewOption, setViewOption] = useState<ViewOption>(ViewOption.Card);
  const [showNewCollectionModal, setShowNewCollectionModal] = useState(false);
  const [showNewSchemaFilterDrawer, setShowNewSchemaFilterDrawer] =
    useState(false);
  const [showNewRuleDrawer, setShowNewRuleDrawer] = useState(false);
  const [cloningRuleId, setCloningRuleId] = useState<string | undefined>();
  const [showEditCollectionModal, setShowEditCollectionModal] = useState(false);
  const [showEditRuleDrawer, setShowEditRuleDrawer] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>();
  const [editingCollectionId, setEditingCollectionId] = useState<
    string | undefined
  >();
  const [showEditSchemaFilterDrawer, setShowEditSchemaFilterDrawer] =
    useState(false);
  const [editingSchemaFilterId, setEditingSchemaFilterId] = useState<
    string | undefined
  >();

  const startJobWithPolling = (ruleId?: string, attributes?: ReportData) => {
    const collection = dataConsistency.selectedCollection;
    if (!collection?.externalId) return;
    const effectiveAttributes =
      attributes ?? parseReportTemplate(collection.reportTemplate);
    const hasAttributes = Object.values(effectiveAttributes).some(
      (v) => v.length > 0
    );
    startConsistencyJob(
      collection.externalId,
      ruleId,
      hasAttributes ? effectiveAttributes : undefined
    );
    setPolling(true);
    clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(() => setPolling(false), POLL_TIMEOUT_MS);
  };

  return {
    ...dataConsistency,
    schemas,
    generateReport: (attributes?: ReportData) =>
      startJobWithPolling(undefined, attributes),
    runSingleRule: (ruleId: string) => startJobWithPolling(ruleId),
    isGeneratingReport,
    showCollectionInfo,
    toggleCollectionInfo: () => setShowCollectionInfo((prev) => !prev),
    viewOption,
    setViewOption,
    showNewCollectionModal,
    openNewCollectionModal: () => setShowNewCollectionModal(true),
    closeNewCollectionModal: () => setShowNewCollectionModal(false),
    showNewSchemaFilterDrawer,
    openNewSchemaFilterDrawer: () => setShowNewSchemaFilterDrawer(true),
    closeNewSchemaFilterDrawer: () => setShowNewSchemaFilterDrawer(false),
    showNewRuleDrawer,
    openNewRuleDrawer: () => setShowNewRuleDrawer(true),
    closeNewRuleDrawer: () => {
      setShowNewRuleDrawer(false);
      setCloningRuleId(undefined);
    },
    openCloneRuleDrawer: (id: string) => {
      setCloningRuleId(id);
      setShowNewRuleDrawer(true);
    },
    cloningRuleId,
    showEditRuleDrawer,
    openEditRuleDrawer: (id: string) => {
      setEditingRuleId(id);
      setShowEditRuleDrawer(true);
    },
    closeEditRuleDrawer: () => setShowEditRuleDrawer(false),
    editingRuleId,
    showEditCollectionModal,
    openEditCollectionModal: (id: string) => {
      setEditingCollectionId(id);
      setShowEditCollectionModal(true);
    },
    closeEditCollectionModal: () => setShowEditCollectionModal(false),
    editingCollectionId,
    showEditSchemaFilterDrawer,
    openEditSchemaFilterDrawer: (id: string) => {
      setEditingSchemaFilterId(id);
      setShowEditSchemaFilterDrawer(true);
    },
    closeEditSchemaFilterDrawer: () => setShowEditSchemaFilterDrawer(false),
    editingSchemaFilterId,
  };
};
